import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TDS_SECTIONS, findTdsSection, type WriteOffReason } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { Button, Input, Money, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, Textarea } from '@/features/common/ui';

/**
 * Closing the last of an invoice: writing off a remainder, or recording the
 * TDS a customer deducted on a payment that was matched before it was known.
 *
 * ── WHY TWO SHEETS ON ONE INVOICE ──
 * After a migration an invoice is short by one of two things. Thirty paise
 * the customer rounded away is money nobody will send - it is written off,
 * as rounding, and the invoice is paid. ₹1,761.38 the customer withheld as
 * TDS is money that went to the government against the seller's own tax -
 * it is recorded on the payment that arrived short, the invoice takes the
 * deduction, and the register carries it for 26AS. The two look alike on
 * the balance and are opposite in the books, which is why each has its own
 * words and neither is a button that "clears the balance".
 */

const invalidate = (queryClient: ReturnType<typeof useQueryClient>, orgId: string | undefined, invoiceId: string) => {
  for (const key of ['invoice', 'invoice-document', 'invoice-payments', 'invoices', 'payments', 'overview', 'dashboard', 'reports']) {
    void queryClient.invalidateQueries({ queryKey: [key, orgId] });
  }
  void queryClient.invalidateQueries({ queryKey: ['invoice', orgId, invoiceId] });
};

const REASONS: Array<{ value: WriteOffReason; label: string; hint: string }> = [
  { value: 'rounding', label: 'Rounding', hint: 'The customer paid a rounded figure; the paise will never arrive.' },
  { value: 'bad_debt', label: 'Bad debt', hint: 'Given up on. The sale stays on the books; the money is not coming.' },
  { value: 'other', label: 'Other', hint: 'Say why in the note.' },
];

export function WriteOffSheet({
  invoice,
  onClose,
}: {
  invoice: { id: string; number: string; balanceDue: string; currency: string };
  onClose: () => void;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(invoice.balanceDue);
  const [reason, setReason] = useState<WriteOffReason>(Number(invoice.balanceDue) < 1 ? 'rounding' : 'bad_debt');
  const [note, setNote] = useState('');
  const writeOff = useMutation({
    mutationFn: () => api.post(`/invoices/${invoice.id}/write-off`, { amount: amount.trim() || undefined, reason, note: note.trim() || undefined }),
    onSuccess: () => {
      invalidate(queryClient, org?.id, invoice.id);
      onClose();
    },
  });
  const error = writeOff.error instanceof ApiError ? writeOff.error : undefined;
  const chosen = REASONS.find((r) => r.value === reason);

  return (
    <Sheet label={`Write off ${invoice.number}`} onClose={onClose} testId="write-off">
      <SheetHeader title={`Write off the remainder of ${invoice.number}`} onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          writeOff.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            <Money value={invoice.balanceDue} currency={invoice.currency} /> is still owed. Writing it off closes the invoice without money
            arriving: the sale stays on the books, nothing is received, and the customer statement shows the write-off as its own line.
          </p>
          <Input label="Amount to write off" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} error={error?.fieldError('amount')} data-testid="write-off-amount" />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="write-off-reason" className="text-sm font-medium">
              Reason
            </label>
            <select
              id="write-off-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as WriteOffReason)}
              className="min-h-11 rounded-md border border-input bg-background px-3 text-base shadow-sm sm:min-h-9 sm:text-sm"
              data-testid="write-off-reason"
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {chosen ? <p className="text-xs text-muted-foreground">{chosen.hint}</p> : null}
          </div>
          <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          {error && !error.fieldError('amount') ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={writeOff.isPending} data-testid="write-off-go">
            Write off
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

export function PaymentTdsSheet({
  invoiceId,
  payment,
  currency,
  balanceDue,
  onClose,
}: {
  invoiceId: string;
  payment: { id: string; number: string; amount: string };
  currency: string;
  /** What the invoice still owes - the most the deduction can discharge here. */
  balanceDue: string;
  onClose: () => void;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [section, setSection] = useState('');
  const [rate, setRate] = useState('');
  const [amount, setAmount] = useState(balanceDue);
  const [tan, setTan] = useState('');
  const record = useMutation({
    mutationFn: () =>
      api.post(`/payments/${payment.id}/tds`, {
        tds: { section, rate: Number(rate), amount: amount.trim(), ...(tan.trim() ? { deductorTan: tan.trim().toUpperCase() } : {}) },
      }),
    onSuccess: () => {
      invalidate(queryClient, org?.id, invoiceId);
      void queryClient.invalidateQueries({ queryKey: ['withholding', org?.id] });
      onClose();
    },
  });
  const error = record.error instanceof ApiError ? record.error : undefined;
  const suggested = findTdsSection(section)?.rate;
  const incomplete = !section || !rate || !amount.trim();

  return (
    <Sheet label={`TDS on ${payment.number}`} onClose={onClose} testId="payment-tds">
      <SheetHeader title={`TDS deducted on ${payment.number}`} onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          if (!incomplete) record.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            The customer paid <Money value={payment.amount} currency={currency} /> and kept back tax. Recording it here discharges the
            invoice by that amount - the money went to the government against your own tax, not to the customer - and puts the
            deduction in the TDS register to reconcile with Form 26AS.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <label className="block text-sm">
              <span className="mb-1.5 block font-medium">Section</span>
              <select
                className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base sm:min-h-9 sm:text-sm"
                value={section}
                onChange={(e) => {
                  setSection(e.target.value);
                  const s = findTdsSection(e.target.value);
                  if (s) setRate(String(s.rate));
                }}
                data-testid="payment-tds-section"
              >
                <option value="">Choose…</option>
                {TDS_SECTIONS.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} — {s.label}
                  </option>
                ))}
              </select>
            </label>
            <Input label="Rate %" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} hint={suggested !== undefined ? `Usually ${suggested}%` : undefined} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <Input label="TDS deducted" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} hint="What they actually kept back." data-testid="payment-tds-amount" />
            <Input label="Their TAN" value={tan} onChange={(e) => setTan(e.target.value.toUpperCase())} hint="On their Form 16A." />
          </div>
          {error ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={record.isPending} disabled={incomplete} data-testid="payment-tds-go">
            Record TDS
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
